<template>
  <div v-if="store">
    <ShowPageHeader backLabel="Points de vente" backRoute="/stores" :title="store.salesChannel || '—'">
      <template #badges>
        <span
          v-if="store.organization?.name"
          class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
        >
          {{ store.organization.name }}
        </span>
        <FieldSpan
          translation-prefix="store.status"
          :style-to-add="getStatusStyleGetter(store.status)"
          :value="store.status"
        />
      </template>
      <template v-if="!isStoreSetup" #actions>
        <SecondaryButton v-if="allowedToCreateMandate(store)" @click="openInvoiceMandateSetupModal = true">
          Demander un mandat de prélèvement
        </SecondaryButton>
        <SecondaryButton v-if="isMandateToSetup(store.mandate)" @click="openResendModal = true">
          Renvoyer le lien du mandat
        </SecondaryButton>
        <SecondaryButton
          v-if="isMandateToSetup(store.mandate) || isMandateActive(store.mandate)"
          @click="openCancelModal = true"
        >
          {{ $t(isMandateActive(store.mandate) ? "mandate.cancel_button" : "mandate.cancel_request_button") }}
        </SecondaryButton>
        <SecondaryButton @click="openCreateEventNoteModal = true">Nouvelle activité</SecondaryButton>
        <CreateButton @click="createProfileModalOpen = true">Nouvel utilisateur</CreateButton>
      </template>
    </ShowPageHeader>

    <!-- WIZARD MODE -->
    <StoreOnboarding v-if="isStoreSetup" :store="store" @store-updated="getStore" @store-activated="getStore" />

    <!-- NORMAL MODE -->
    <StoreActive v-else :store="store" :store-id="storeId" @store-updated="getStore" />

    <!-- Page-level modals -->
    <InvoiceMandateSetupModal
      :open="openInvoiceMandateSetupModal"
      @sendEmail="sendInvoiceMandateForm"
      @close="openInvoiceMandateSetupModal = false"
    />

    <ResendMandateLinkModal
      :open="openResendModal"
      :email="store.mandate?.responsibleEmail"
      :loading="mandateActionLoading"
      @confirm="resendMandateLink"
      @close="openResendModal = false"
    />
    <CancelMandateModal
      :open="openCancelModal"
      :email="store.mandate?.responsibleEmail"
      :active="isMandateActive(store.mandate)"
      :loading="mandateActionLoading"
      @confirm="cancelMandateRequest"
      @close="openCancelModal = false"
    />

    <StoreEventNoteCreateModal
      :open="openCreateEventNoteModal"
      :store="store"
      @close="openCreateEventNoteModal = false"
      @addedNote="onEventNoteAdded"
    />

    <FullScreenModal :open="createProfileModalOpen" @close="createProfileModalOpen = false">
      <MerchantProfileFormNew
        :organizationId="store.organization?.id"
        :storeId="storeId"
        @success="newProfileCreated"
      />
    </FullScreenModal>
  </div>
</template>

<script>
import StoreOnboarding from "@/components/store/details/onboarding/StoreOnboarding.vue";
import StoreActive from "@/components/store/details/StoreActive.vue";
import MerchantProfileFormNew from "@/components/merchant_profiles/MerchantProfileFormNew.vue";
import InvoiceMandateSetupModal from "@/components/mandate/InvoiceMandateSetupModal.vue";
import CancelMandateModal from "@/components/mandate/CancelMandateModal.vue";
import ResendMandateLinkModal from "@/components/mandate/ResendMandateLinkModal.vue";
import StoreEventNoteCreateModal from "@/components/store/details/StoreEventNoteCreateModal.vue";

import { CreateButton, FieldSpan, FullScreenModal, SecondaryButton, ShowPageHeader } from "@estaly/ui";

import { mapActions, mapGetters } from "vuex";
import { toCamelCase } from "@/utils/utils";

export default {
  components: {
    StoreOnboarding,
    StoreActive,
    StoreEventNoteCreateModal,
    FieldSpan,
    ShowPageHeader,
    InvoiceMandateSetupModal,
    CancelMandateModal,
    ResendMandateLinkModal,
    MerchantProfileFormNew,
    CreateButton,
    FullScreenModal,
    SecondaryButton,
  },
  data() {
    return {
      store: null,
      openInvoiceMandateSetupModal: false,
      openResendModal: false,
      openCancelModal: false,
      mandateActionLoading: false,
      openCreateEventNoteModal: false,
      createProfileModalOpen: false,
    };
  },
  async beforeMount() {
    await this.getStore();
    await this.fetchMatchedContractsData();
  },
  computed: {
    ...mapGetters("stores", {
      allowedToCreateMandate: "allowedToCreateMandate",
      isStoreSetupGetter: "isStoreSetup",
      getStatusStyleGetter: "getStoreStatusFieldDisplayStyle",
    }),
    ...mapGetters("mandates", ["isMandateToSetup", "isMandateActive"]),

    storeId() {
      return Number(this.$route.params.id);
    },
    isStoreSetup() {
      return this.isStoreSetupGetter(this.store);
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("stores", ["fetch"]),
    ...mapActions("mandates", {
      sendMandateForm: "sendInvoiceMandateForm",
      sendNewLink: "sendNewLink",
      cancelMandate: "cancel",
    }),
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("matchedContracts", { fetchMatchedContracts: "fetchIndex" }),
    ...mapActions("merchantProfiles", { fetchMerchantProfiles: "fetchIndex" }),

    async getStore() {
      await this.withLoader(async () => {
        const response = await this.fetch({
          id: this.storeId,
          includes: [
            "store_feature_configuration",
            "matched_contracts.contract",
            "matched_contracts",
            "organization",
            "billing_entity",
            "mandate",
            "discount_store_relation.discount",
            "sellers",
            "challenge",
            "estaly_api_accounts",
            "referent",
            "sellers_profile",
            "estaly_api_accounts",
            "store_event_notes",
            "store_event_notes.estaly_manager",
          ],
        });
        this.store = toCamelCase(response.data);
      });
    },
    async fetchMatchedContractsData() {
      await this.fetchMatchedContracts({
        storeId: this.storeId,
        page: 1,
        perPage: 500,
        includes: ["contract", "business_vertical"],
        enabled: true,
      });
    },
    async sendInvoiceMandateForm(email) {
      const { success } = await this.sendMandateForm({
        mandateSubjectType: "Store",
        mandateSubjectId: this.storeId,
        email,
      });
      if (!success) return;
      this.openInvoiceMandateSetupModal = false;
      await this.getStore();
    },

    async resendMandateLink() {
      if (this.mandateActionLoading) return;
      this.mandateActionLoading = true;
      try {
        const { success } = await this.sendNewLink({ mandateId: this.store.mandate.id });
        if (!success) return;
        this.openResendModal = false;
        await this.getStore();
      } finally {
        this.mandateActionLoading = false;
      }
    },

    async cancelMandateRequest() {
      if (this.mandateActionLoading) return;
      this.mandateActionLoading = true;
      try {
        const { success } = await this.cancelMandate({ mandateId: this.store.mandate.id });
        if (!success) return;
        this.openCancelModal = false;
        this.openResendModal = false;
        await this.getStore();
      } finally {
        this.mandateActionLoading = false;
      }
    },

    newProfileCreated() {
      this.createProfileModalOpen = false;
      this.fetchMerchantProfiles({
        organizationIds: this.store.organization?.id ? [this.store.organization.id] : [],
        storeIds: this.storeId ? [this.storeId] : [],
      });
    },
    async onEventNoteAdded() {
      this.openCreateEventNoteModal = false;
      await this.getStore();
    },
  },
};
</script>
